import { profileStore } from './profile';
import { messageStore } from './messages';
import { conversationStore } from './conversations';
import { suggestionStore, type Suggestion } from './suggestions';
import { messageMetadataStore } from './messageMetadataStore';
import { MessageMetadata } from '../../types/chat';

// Export types
export type { Suggestion, MessageMetadata };

// Create a strongly-typed storage interface
export interface Storage {
  // Profile operations
  getProfile(walletAddress: string): Promise<any>;
  setProfile(walletAddress: string, profile: any): Promise<void>;
  removeProfile(walletAddress: string): Promise<void>;

  // Message operations
  getMessage(messageId: string): Promise<any>;
  setMessage(messageId: string, message: any): Promise<void>;
  removeMessage(messageId: string): Promise<void>;
  getMessages(messageIds: string[]): Promise<any[]>;

  // Conversation operations
  getConversations(walletAddress: string): Promise<any[]>;
  getConversationMessages(walletAddress: string, conversationId: string): Promise<any[]>;
  setConversation(walletAddress: string, conversation: any, messageIds?: string[]): Promise<void>;
  addMessageToConversation(walletAddress: string, conversationId: string, message: any): Promise<void>;
  removeConversation(walletAddress: string, conversationId: string): Promise<void>;
  updateConversation(walletAddress: string, conversationId: string, updates: any): Promise<void>;

  // Suggestion operations
  getSuggestions(): Promise<Suggestion[]>;
  setSuggestions(suggestions: Suggestion[]): Promise<void>;
  initializeDefaultSuggestions(): Promise<Suggestion[]>;
  updateSuggestion(id: string, content: string): Promise<void>;
  
  // Message metadata operations
  getMessageMetadata(messageId: string): Promise<MessageMetadata | null>;
  saveMessageMetadata(messageId: string, metadata: MessageMetadata): Promise<void>;
  extractAndSaveMetadata(message: any): Promise<void>;


  // Clear all storage
  clear(): Promise<void>;
}

// Create and export the storage instance
export const storage: Storage = {
  // Profile operations
  getProfile: profileStore.getProfile,
  setProfile: profileStore.setProfile,
  removeProfile: profileStore.removeProfile,

  // Message operations
  getMessage: messageStore.getMessage,
  setMessage: messageStore.setMessage,
  removeMessage: messageStore.removeMessage,
  getMessages: messageStore.getMessages,

  // Conversation operations
  getConversations: conversationStore.getConversations,
  getConversationMessages: conversationStore.getConversationMessages,
  setConversation: conversationStore.setConversation,
  addMessageToConversation: conversationStore.addMessageToConversation,
  removeConversation: conversationStore.removeConversation,
  updateConversation: conversationStore.updateConversation,

  // Suggestion operations
  getSuggestions: () => suggestionStore.getSuggestions(),
  setSuggestions: (suggestions) => suggestionStore.setSuggestions(suggestions),
  initializeDefaultSuggestions: () => suggestionStore.initializeDefaultSuggestions(),
  updateSuggestion: (id, content) => suggestionStore.updateSuggestion(id, content),
  
  // Message metadata operations
  getMessageMetadata: (messageId) => messageMetadataStore.getMessageMetadata(messageId),
  saveMessageMetadata: (messageId, metadata) => messageMetadataStore.saveMessageMetadata(messageId, metadata),
  extractAndSaveMetadata: (message) => messageMetadataStore.extractAndSaveMetadata(message),


  // Clear all storage
  async clear() {
    await Promise.all([
      profileStore.clear(),
      messageStore.clear(),
      conversationStore.clear(),
      suggestionStore.clear(),
      messageMetadataStore.clearAllMessageMetadata()
    ]);
  }
};
